import React from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useParams } from 'react-router-dom';
import { Input } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { useLocalStorage, useMediaQuery } from 'usehooks-ts';
import { DropDownList } from '@progress/kendo-react-dropdowns';

import { Tasks } from './tasks';
import { Areas } from './areas';
import { Zones } from './zones';
import { Images } from './images';
import {
  UpdateGameDocState,
  GetAssetRoutesByMapId
} from '../../../utils/game-document';
import { Annotations } from './annotations';
import { MapConfigStore } from '../../../pages/game-designer';
import { GameDocumentContext } from '../../../contexts/game-document';
import { ZoneAssetId } from '../../../types/game-document/entities/zone';
import { MenuList } from '../../../features/game-document/maps/map-images-window';

type MapAction = 'Zone' | 'Task' | 'Area' | 'Annotation' | 'Images' | '';

const selectOptions = [
  { text: 'All', value: 'ALL' },
  { text: 'Task', value: 'TASK' },
  { text: 'Zone map', value: 'ZONE' },
  { text: 'Area', value: 'AREA' },
  { text: 'Image', value: 'IMAGE' }
];

const contentOptions = [
  { text: 'Task', value: 'Task' },
  { text: 'Zone map', value: 'Zone' },
  { text: 'Area', value: 'Area' },
  { text: 'Image', value: 'Image' },
  { text: 'Annotation', value: 'Annotation' }
];

const ShowContentButton: React.FC<{
  text: string;
  index: number;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ active, index, disabled, text, onClick }) => {
  return (
    <button
      type={'button'}
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      style={{
        flex: 1,
        border: 'none',
        padding: '8px 4px',
        fontFamily: 'inherit',
        fontWeight: active ? 600 : 400,
        color: active ? '#00406b' : '#6b7280',
        background: active ? '#e1f1f9' : '#e0e0e0',
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderLeft: index === 0 ? 'none' : `1.5px solid #00406b`
      }}>
      {text}
    </button>
  );
};

export const MapContents: React.FC<{
  isEmpty: boolean;
  showAllTasks: boolean;
  showTaskTitle: boolean;
  selectedPanel: MapAction;
  selectedAllObjectType: string | undefined;
  onShowAllTasks: () => void;
  toggleShowTaskTtile: () => void;
  toggleActionsRouteDialog: () => void;
  toggleActionsMapImageDialog: () => void;
  onSelectedPanel?: (e: MapAction) => void;
  onSelectAllAssets: (selected: string) => void;
  handleAreaMenuSelected: (id: string, menu: MenuList) => void;
  handleTaskMenuSelected: (id: string, menu: MenuList) => void;
  handleZoneMenuSelected: (id: string, menu: MenuList) => void;
  handleRouteMenuSelected: (id: string, menu: MenuList) => void;
  setSelectedRouteId: React.Dispatch<React.SetStateAction<string>>;
  handleMapImagesMenuSelected: (id: string, menu: MenuList) => void;
  setSelectedPanel: (value: React.SetStateAction<MapAction>) => void;
  setSelectedMapImageId: React.Dispatch<React.SetStateAction<string>>;
}> = ({
  isEmpty,
  showAllTasks,
  showTaskTitle,
  selectedPanel,
  selectedAllObjectType,
  onShowAllTasks,
  onSelectedPanel,
  setSelectedPanel,
  onSelectAllAssets,
  setSelectedRouteId,
  toggleShowTaskTtile,
  setSelectedMapImageId,
  handleZoneMenuSelected,
  handleAreaMenuSelected,
  handleTaskMenuSelected,
  handleRouteMenuSelected,
  toggleActionsRouteDialog,
  toggleActionsMapImageDialog,
  handleMapImagesMenuSelected
}) => {
  const isDesktop = useMediaQuery('(min-width: 1920px)');

  const { gameId, zoneId } = useParams();
  const [mapConfig, setMapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = React.useContext(GameDocumentContext);

  const [selectedContent, setSelectedContent] = React.useState(
    contentOptions.find((c) => c.value === mapConfig.selectedAddContent) ??
      contentOptions[0]
  );
  const [selectedSelect, setSelectedSelect] = React.useState(
    selectOptions.find((s) => s.value === mapConfig.selectedMoveContent) ??
      selectOptions[0]
  );
  const [searchContentText, setSearchContentText] = React.useState<string>(
    mapConfig.contentSearch ?? ''
  );

  const mapType = React.useMemo(() => {
    const maps = state.gameDocument?.assets.maps ?? [];
    if (zoneId) {
      const zoneAsId = state.gameDocument?.rules.worldMap.zones.find(
        (z) => z.zoneAssId === zoneId
      );
      if (zoneAsId && zoneAsId.mapAssId) {
        return (
          maps.find((m) => m.id === zoneAsId.mapAssId)?.type ?? 'openStreetMap'
        );
      }
    }

    return maps[0].type;
  }, [state.gameDocument, zoneId]);

  const selectionOptions = React.useMemo(() => {
    let result = cloneDeep(selectOptions);

    if (mapType === 'illustrated')
      result = result.filter((r) => r.value !== 'AREA');
    if (zoneId) result = result.filter((r) => r.value !== 'ZONE');

    return result;
  }, [zoneId, mapType]);

  const contents = React.useMemo(() => {
    let result = cloneDeep(contentOptions);

    if (mapType === 'illustrated')
      result = result.filter((r) => r.value !== 'Area');
    if (zoneId) result = result.filter((r) => r.value !== 'Zone');

    return result;
  }, [zoneId, mapType]);

  const routes = React.useMemo(() => {
    if (!state.gameDocument) return [];

    let mapAssetId = '';

    if (zoneId) {
      mapAssetId =
        state.gameDocument?.rules.worldMap.zones.find(
          (zone: ZoneAssetId) => zone.zoneAssId === zoneId
        )?.mapAssId ?? '';
    } else {
      mapAssetId = state.gameDocument?.rules?.worldMap?.mapAssId!;
    }
    const routes = GetAssetRoutesByMapId(state.gameDocument!, mapAssetId)!;

    return routes;
  }, [state.gameDocument, zoneId]);

  const onSelectChange = () => {
    if (!selectedAllObjectType) {
      onSelectAllAssets(selectedSelect.value);
    } else {
      onSelectAllAssets('');
    }
  };

  const onAddContent = () => {
    if (!selectedPanel) {
      setSelectedPanel(selectedContent.value as MapAction);
      onSelectedPanel && onSelectedPanel(selectedContent.value as MapAction);
      if (selectedContent.value === 'Image') {
        setSelectedMapImageId('');
        toggleActionsMapImageDialog();
      }

      if (selectedContent.value === 'Annotation') {
        setSelectedRouteId('');
        toggleActionsRouteDialog();
      }
    } else {
      setSelectedPanel('');
      onSelectedPanel && onSelectedPanel('');
    }
  };

  return (
    <div
      className={'d-flex flex-column'}
      style={{
        marginTop: '8px',
        gap: '8px'
      }}>
      <div
        style={{
          padding: '0 16px'
        }}>
        {!isEmpty && (
          <div>
            <span
              style={{
                fontSize: '12px'
              }}>
              <span className={'fw-bold'}>Move Content</span>
              {isDesktop && (
                <span
                  className={'ml-2'}
                  style={{
                    fontSize: '12px'
                  }}>
                  Bulk select and drag to reposition
                </span>
              )}
            </span>
            <div className={'d-flex gap-2 w-100'}>
              <div className={'d-flex w-100'}>
                <DropDownList
                  data={selectionOptions}
                  textField={'text'}
                  dataItemKey={'value'}
                  value={selectedSelect}
                  onChange={(e) => {
                    setSelectedSelect(e.value);
                    setMapConfig((prev) => ({
                      ...prev,
                      selectedMoveContent: e.value.value
                    }));
                    if (selectedAllObjectType !== '')
                      onSelectAllAssets(e.value.value);
                  }}
                  style={{
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0
                  }}
                />
                <Button
                  onClick={onSelectChange}
                  themeColor={selectedAllObjectType !== '' ? 'warning' : 'dark'}
                  style={{
                    flexShrink: 0,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    ...(selectedAllObjectType === '' && {
                      backgroundColor: '#003A64'
                    })
                  }}>
                  <span
                    className={'material-symbols-outlined'}
                    style={{
                      fontSize: '16px'
                    }}>
                    {!selectedAllObjectType ? 'open_with' : 'remove_selection'}
                  </span>
                  {isDesktop && (
                    <span className={'ml-2'}>
                      {!selectedAllObjectType ? 'Move' : 'Cancel'}
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div>
          <span
            style={{
              fontSize: '12px'
            }}>
            <span className={'fw-bold'}>Add Content</span>
            {isDesktop && (
              <span
                className={'ml-2'}
                style={{
                  fontSize: '12px'
                }}>
                Select type and position on map
              </span>
            )}
          </span>

          <div className={'d-flex gap-2 w-100'}>
            <div className={'d-flex w-100'}>
              <DropDownList
                disabled={selectedPanel !== ''}
                data={contents}
                textField={'text'}
                dataItemKey={'value'}
                value={selectedContent}
                onChange={(e) => {
                  setSelectedContent(e.value);
                  setMapConfig((prev) => ({
                    ...prev,
                    selectedAddContent: e.value.value
                  }));
                }}
                style={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0
                }}
              />
              <Button
                themeColor={!selectedPanel ? 'dark' : 'warning'}
                onClick={onAddContent}
                style={{
                  flexShrink: 0,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  ...(selectedPanel === '' && {
                    backgroundColor: '#003A64'
                  })
                }}>
                <span
                  className={'material-symbols-outlined'}
                  style={{
                    fontSize: '16px'
                  }}>
                  add_circle
                </span>
                {isDesktop && (
                  <span className={'ml-2'}>
                    {!selectedPanel ? 'Add' : 'Cancel'}{' '}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className={'mt-2'}>
          <span
            style={{
              fontSize: '12px'
            }}>
            <span className={'fw-bold'}>Show Content</span>
            {isDesktop && (
              <span
                className={'ml-2'}
                style={{
                  fontSize: '12px'
                }}>
                Choose what is visible on the Game Designer map
              </span>
            )}
          </span>

          <div
            className={'d-flex w-100 mt-2'}
            style={{
              display: 'flex',
              border: `1.5px solid #00406b`,
              borderRadius: 8,
              overflow: 'hidden'
            }}>
            <ShowContentButton
              index={0}
              text={'Tasks'}
              active={showAllTasks}
              onClick={() => {
                setMapConfig((prev) => ({
                  ...prev,
                  showAllTasks: !showAllTasks
                }));
                onShowAllTasks();
              }}
            />
            <ShowContentButton
              index={1}
              text={'Titles'}
              disabled={!showAllTasks}
              onClick={() => {
                setMapConfig((prev) => ({
                  ...prev,
                  showTaskTitle: !showTaskTitle
                }));
                toggleShowTaskTtile();
              }}
              active={showTaskTitle && showAllTasks}
            />
            <ShowContentButton
              index={2}
              text={'Annotations'}
              onClick={() => {
                let showAllValue = routes.every((r) => r.isVisible)
                  ? false
                  : true;

                const resultRoutes = cloneDeep(routes).map((r) => ({
                  ...r,
                  isVisible: showAllValue
                }));

                setState((prev) => {
                  if (!prev.gameDocument) return prev;
                  const result = cloneDeep(prev.gameDocument);
                  result.assets.routes = resultRoutes;
                  return UpdateGameDocState(prev, result);
                });
              }}
              active={routes.every((r) => r.isVisible)}
            />
          </div>
        </div>

        {!isEmpty && (
          <div>
            <span
              style={{
                fontSize: '12px'
              }}>
              <span className={'fw-bold'}>Find Content</span>
              {isDesktop && (
                <span
                  className={'ml-2'}
                  style={{
                    fontSize: '12px'
                  }}>
                  Browse or search for map content
                </span>
              )}
            </span>

            <div className={'d-flex gap-2 w-100'}>
              <Input
                type={'text'}
                className={'form-control mb-1'}
                value={searchContentText}
                placeholder={'Search content'}
                onChange={(e) => {
                  setSearchContentText(e.value);
                  setMapConfig((prev) => ({ ...prev, contentSearch: e.value }));
                }}
              />
            </div>
          </div>
        )}
      </div>

      <div id={'map-contents'} className={'d-flex flex-column'}>
        <Tasks
          showAll={showAllTasks}
          showTaskTitle={showTaskTitle}
          searchContentText={searchContentText}
          handleTaskMenuSelected={handleTaskMenuSelected}
        />
        <Images
          mapType={mapType}
          searchContentText={searchContentText}
          handleMapImagesMenuSelected={handleMapImagesMenuSelected}
        />
        {mapType !== 'illustrated' && (
          <Areas
            searchContentText={searchContentText}
            handleAreaMenuSelected={handleAreaMenuSelected}
          />
        )}
        {!zoneId && (
          <Zones
            searchContentText={searchContentText}
            handleZoneMenuSelected={handleZoneMenuSelected}
          />
        )}
        <Annotations
          routes={routes}
          searchContentText={searchContentText}
          handleRouteMenuSelected={handleRouteMenuSelected}
        />
      </div>
    </div>
  );
};
